import {useMemo} from 'react';
import type {SpotlightMaskCoordinate, SpotlightMaskInsets} from './types';

export function useMaskCoordinates(nodes: HTMLElement[], insets?: SpotlightMaskInsets) {
  return useMemo(() => {
    if (!nodes.length) {
      return [];
    }

    let rects = nodes.map((node) => node.getBoundingClientRect()).sort((a, b) => a.top - b.top);

    if (insets) {
      const {top = 0, bottom = 0, left = 0, right = 0} = insets;
      rects = rects.map((rect) =>
        DOMRect.fromRect({
          x: rect.left + left,
          y: rect.top + top,
          width: rect.width - right - left,
          height: rect.height - bottom - top,
        })
      );
    }

    // Get the dimensions of the viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Generate an array of overlay div positions
    const coordinates: SpotlightMaskCoordinate[] = [];

    // Add the top area (above the first node)
    if (rects[0].top > 0) {
      coordinates.push({
        key: 'fue-mask-top',
        top: 0,
        left: 0,
        width: window.innerWidth,
        height: rects[0].top,
      });
    }

    // Add the bottom area (below the last node)
    if (rects[rects.length - 1].bottom < viewportHeight) {
      coordinates.push({
        key: 'fue-mask-bottom',
        top: rects[rects.length - 1].bottom,
        left: 0,
        width: window.innerWidth,
        height: viewportHeight - rects[rects.length - 1].bottom,
      });
    }

    // Add the left and right areas, and gaps between nodes
    rects.forEach((rect, index) => {
      // Add the left area for each node
      if (rect.left > 0) {
        coordinates.push({
          key: `fue-mask-left-${index}`,
          top: rect.top,
          left: 0,
          width: rect.left,
          height: rect.height,
        });
      }

      // Add the right area for each node
      if (rect.right < viewportWidth) {
        coordinates.push({
          key: `fue-mask-right-${index}`,
          top: rect.top,
          left: rect.right,
          width: viewportWidth - rect.right,
          height: rect.height,
        });
      }

      // Add the gaps between nodes (if any)
      if (index > 0 && rect.top > rects[index - 1].bottom) {
        coordinates.push({
          key: `fue-mask-gap-${index}`,
          top: rects[index - 1].bottom,
          left: 0,
          width: window.innerWidth,
          height: rect.top - rects[index - 1].bottom,
        });
      }
    });

    return coordinates;
  }, [nodes, insets]);
}
